import "./AidwopsAiAssistant.scss";
import {
    type KeyboardEvent,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import DOMPurify from "dompurify";
import { marked } from "marked";
import { EonUiPreloaderSquares } from "@eon-ui/eon-ui-components-react";
import { AiLoader } from "@me8eon/ai_loader";
import { Icon } from "@me8eon/icons";
import { useJwtToken } from "@me8eon/react_authentication";
import { api } from "./expertApi";
import type {
    AskContext,
    HitlHealth,
    HitlQueueCard,
    ItemAction,
    ItemDetail,
    ItemStatus,
    Persona,
} from "./aidwops.types";
import useAidwopsStore from "./AidwopsStore";
import type { RequestKind } from "./expert-dashboard/RaiseRequestForm";
import { SearchLanding } from "./expert-dashboard/SearchLanding";
import SearchInputBox from "./expert-dashboard/SearchInputBox";
import ExpertQueueView from "./expert-dashboard/ExpertQueueView";
import type { QueueRole } from "./expert-dashboard/ExpertQueueFilters";
import { ChatUiBlock } from "./expert-dashboard/ChatUiBlock";
import { QueueItemDetailCard } from "./expert-dashboard/ExpertQueueItemDetailCard";
import { IsoRequestDetail } from "./expert-dashboard/IsoRequestDetail";
import { AIDWOPS_ASSISTANT_PROMPTS } from "./expert-dashboard/ExpertDashboard.constants";
import AidwopsAiAssistantWhitelistRequestResponse from "./expert-dashboard/AidwopsAiAssistantWhitelistRequestResponse";
import AidwopsAiAssistantSslBypassRequestResponse from "./expert-dashboard/AidwopsAiAssistantSslBypassRequestResponse";

/** Shown next to the ISO's decision when the Jira issue could not be moved along with it automatically. */
const ISO_JIRA_NOT_MOVED =
    "The decision is recorded here, but the Jira issue could not be moved to the matching status automatically. Please move it by hand.";

/** Statuses that change without the person doing anything: with the ISO, approved and about to be written, being written. */
const POLLED_STATUSES = new Set<ItemStatus>([
    "waiting_iso",
    "approved",
    "applying",
]);
const ITEM_POLL_MS = 15000;

const SANITIZE_CONFIG = {
    ALLOWED_URI_REGEXP:
        /^(?:(?:(?:f|ht)tps?|mailto):|[^a-z]|[a-z+.\-]+(?:[^a-z+.\-:]|$))/i,
};

function FormattedAssistantContent({ content }: { content: string }) {
    const sanitizedHtml = useMemo(() => {
        const parsedHtml = marked.parse(content, {
            async: false,
            breaks: true,
            gfm: true,
        }) as string;

        return DOMPurify.sanitize(parsedHtml, SANITIZE_CONFIG);
    }, [content]);

    return (
        <div
            className="aidwops-assistant__formatted-content"
            dangerouslySetInnerHTML={{ __html: sanitizedHtml }}
        />
    );
}

interface AidwopsAiAssistantProps {
    initialQuestion?: string;
    onInitialQuestionHandled?: () => void;
    onQueueBannerClick: () => void;
    onOpenExpertQueue: () => void;
    isQueueView?: boolean;
    /** Whose queue this view shows; decisions and refreshes reload that same queue. */
    queuePersona?: Persona;
    /** Which status the segmented "Status" filter above the queue is set to. */
    queueStatus?: "pending" | "decided" | "all";
    /** The Role/Persona/Status filter bar (rendered inside the live ExpertQueueView) is shown when all of these are supplied. */
    queueRole?: QueueRole;
    onQueueRoleChange?: (role: QueueRole) => void;
    onQueuePersonaChange?: (persona: Persona) => void;
    onQueueStatusChange?: (status: "pending" | "decided" | "all") => void;
}

export function AidwopsAiAssistant({
    initialQuestion,
    onInitialQuestionHandled,
    onQueueBannerClick,
    isQueueView = false,
    queuePersona = "all",
    queueStatus = "pending",
    queueRole,
    onQueueRoleChange,
    onQueuePersonaChange,
    onQueueStatusChange,
    onOpenExpertQueue,
}: AidwopsAiAssistantProps) {
    const jwtToken = useJwtToken();
    const aiAssistantSectionRef = useRef<HTMLDivElement>(null);
    const textareaRef = useRef<HTMLTextAreaElement>(null);
    const latestQuestionRef = useRef<HTMLDivElement>(null);
    const initialQuestionSubmittedRef = useRef(false);
    const draft = useAidwopsStore((state) => state.draft);
    const setDraft = useAidwopsStore((state) => state.setDraft);
    const initialPrompts = useAidwopsStore((state) => state.initialPrompts);
    const messages = useAidwopsStore((state) => state.messages);
    const isQueueLoading = useAidwopsStore((state) => state.isQueueLoading);
    const isSubmitting = useAidwopsStore((state) => state.isSubmitting);
    const editingMessageId = useAidwopsStore((state) => state.editingMessageId);
    const setEditingMessageId = useAidwopsStore(
        (state) => state.setEditingMessageId
    );
    const editingDraft = useAidwopsStore((state) => state.editingDraft);
    const setEditingDraft = useAidwopsStore((state) => state.setEditingDraft);
    const selectedItem = useAidwopsStore((state) => state.selectedItem);
    const setSelectedItem = useAidwopsStore((state) => state.setSelectedItem);
    const selectedItemDetail = useAidwopsStore(
        (state) => state.selectedItemDetail
    );
    const setSelectedItemDetail = useAidwopsStore(
        (state) => state.setSelectedItemDetail
    );
    const setQueue = useAidwopsStore((state) => state.setQueue);
    const fetchQueue = useAidwopsStore((state) => state.fetchQueue);
    const isSelectedItemLoading = useAidwopsStore(
        (state) => state.isSelectedItemLoading
    );
    const selectedItemError = useAidwopsStore(
        (state) => state.selectedItemError
    );
    const fetchSelectedItemDetail = useAidwopsStore(
        (state) => state.fetchSelectedItemDetail
    );
    const submitPrompt = useAidwopsStore((state) => state.submitPrompt);

    // The ISO review pane: the engineer's review state above lives in the store (it is also read by chat
    // commands), but the ISO has no such commands yet, so its selection/detail stay local to this component.
    const [isoSelectedId, setIsoSelectedId] = useState<string | null>(null);
    const [isoDetail, setIsoDetail] = useState<ItemDetail | null>(null);
    const [isIsoDetailLoading, setIsIsoDetailLoading] = useState(false);
    const [isoDetailError, setIsoDetailError] = useState<string | null>(null);
    const [isoJiraWarning, setIsoJiraWarning] = useState<string | null>(null);
    // Bumped after every ISO action so the embedded ISO queue remounts and reloads its list.
    const [isoRefreshNonce, setIsoRefreshNonce] = useState(0);
    const isoDetailRequestRef = useRef(0);

    const askContext: AskContext | undefined = isQueueView
        ? { view: "queue", selected: selectedItem }
        : undefined;

    useEffect(() => {
        textareaRef.current?.focus();
    }, []);

    useEffect(() => {
        if (messages[messages.length - 1]?.role !== "user") {
            return;
        }

        latestQuestionRef.current?.scrollIntoView({
            behavior: "smooth",
            block: "start",
        });
    }, [messages]);

    const getAccessToken = async () => {
        const accessToken = await jwtToken();
        if (!accessToken) {
            throw new Error("Unable to acquire access token for expert API");
        }
        return accessToken;
    };

    /** Brings the list up to date after a change. A list that is stale for a moment must not fail a change that already went through. */
    const reloadQueue = async (accessToken: string) => {
        try {
            setQueue(
                await api.getQueue(queuePersona, accessToken),
                queuePersona
            );
        } catch {
            // The next refresh or action reloads it
        }
    };

    /** Does what the person confirmed in the review panel, then shows the item as the backend now has it. */
    const handleItemAction = async (
        action: ItemAction,
        reason: string
    ): Promise<ItemDetail> => {
        if (!selectedItemDetail) {
            throw new Error("No queue item is selected.");
        }

        const { id, version } = selectedItemDetail;
        const accessToken = await getAccessToken();
        let updatedItem: ItemDetail;
        switch (action) {
            case "approve":
            case "decline":
                updatedItem = await api.decideQueueItem(
                    id,
                    action,
                    reason,
                    version,
                    accessToken
                );
                break;
            case "rollback":
                updatedItem = await api.rollbackQueueItem(
                    id,
                    reason,
                    version,
                    accessToken
                );
                break;
            case "close":
                updatedItem = await api.closeQueueItem(
                    id,
                    reason,
                    version,
                    accessToken
                );
                break;
            case "retry":
                updatedItem = await api.retryQueueItem(id, accessToken);
                break;
            default:
                throw new Error("The ISO decides in the ISO queue.");
        }

        setSelectedItemDetail(updatedItem);
        await reloadQueue(accessToken);
        return updatedItem;
    };

    /** A note on the open item's Jira issue. It does not change the request, so there is no list to reload. */
    const handleItemComment = async (text: string): Promise<ItemDetail> => {
        if (!selectedItemDetail) {
            throw new Error("No queue item is selected.");
        }

        const updatedItem = await api.commentOnQueueItem(
            selectedItemDetail.id,
            text,
            await getAccessToken()
        );
        setSelectedItemDetail(updatedItem);
        return updatedItem;
    };

    /** The Raise request form: creates the request, then reloads the list so it is there. */
    const handleQueueRaise = async (
        url: string,
        justification: string,
        kind: RequestKind
    ): Promise<ItemDetail> => {
        const accessToken = await getAccessToken();
        const created = await api.createQueueItem(
            url,
            justification,
            kind,
            accessToken
        );
        await reloadQueue(accessToken);
        return created;
    };

    /** Re-reads the open item and the queue, for an item that changed since it was opened (409). */
    const handleQueueReload = async () => {
        if (!selectedItem) {
            return;
        }

        try {
            const accessToken = await getAccessToken();
            await fetchSelectedItemDetail(selectedItem, accessToken);
            try {
                setQueue(
                    await api.getQueue(queuePersona, accessToken),
                    queuePersona
                );
            } catch {
                // The queue list can be stale for a moment; the item itself is what matters here
            }
        } catch {
            // No token: the panel stays as it is and the person can close it and sign in again
        }
    };

    /** Reads the full detail for an ISO request, dropping an answer that arrives after the person moved on. */
    const loadIsoDetail = async (itemId: string) => {
        const request = ++isoDetailRequestRef.current;
        setIsIsoDetailLoading(true);
        setIsoDetailError(null);
        try {
            const accessToken = await getAccessToken();
            const item = await api.getIsoItem(itemId, accessToken);
            if (request === isoDetailRequestRef.current) {
                setIsoDetail(item);
            }
        } catch (error) {
            if (request === isoDetailRequestRef.current) {
                setIsoDetail(null);
                setIsoDetailError(
                    error instanceof Error ? error.message : String(error)
                );
            }
        } finally {
            if (request === isoDetailRequestRef.current) {
                setIsIsoDetailLoading(false);
            }
        }
    };

    /** Opens the ISO review pane for a request from the embedded ISO queue. */
    const openIsoItem = (item: HitlQueueCard) => {
        setIsoSelectedId(item.id);
        setIsoDetail(null);
        setIsoJiraWarning(null);
        void loadIsoDetail(item.id);
    };

    const closeIsoItem = () => {
        isoDetailRequestRef.current += 1;
        setIsoSelectedId(null);
        setIsoDetail(null);
        setIsoDetailError(null);
        setIsIsoDetailLoading(false);
    };

    // Switching the Role filter (Engineer/ISO) must not leave either side's review panel open on a
    // selection that no longer applies: the engineer's selectedItem lives in the store, the ISO's
    // selection is local to this component, so both have to be cleared here.
    useEffect(() => {
        setSelectedItem(null);
        closeIsoItem();
        // Only queueRole should trigger this; the setters/closeIsoItem follow unstable identities.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [queueRole]);

    /** Does what the ISO confirmed in the review panel, then shows the item as the backend now has it. */
    const handleIsoAction = async (
        action: ItemAction,
        reason: string
    ): Promise<ItemDetail> => {
        if (!isoDetail) {
            throw new Error("No request is selected.");
        }

        const accessToken = await getAccessToken();
        let updated: ItemDetail;
        if (action === "iso_approve" || action === "iso_reject") {
            const result = await api.decideIsoItem(
                isoDetail.id,
                action === "iso_approve" ? "approve" : "reject",
                reason,
                isoDetail.version,
                accessToken
            );
            updated = result.item;
            setIsoJiraWarning(result.jiraUpdated ? null : ISO_JIRA_NOT_MOVED);
        } else {
            throw new Error(
                "Only ISO approval or rejection is available in the ISO queue."
            );
        }

        setIsoDetail(updated);
        setIsoRefreshNonce((nonce) => nonce + 1);
        return updated;
    };

    /** A note on the open request's Jira issue. It does not change the request, so there is no list to reload. */
    const handleIsoComment = async (text: string): Promise<ItemDetail> => {
        if (!isoDetail) {
            throw new Error("No request is selected.");
        }

        const updated = await api.commentOnQueueItem(
            isoDetail.id,
            text,
            await getAccessToken()
        );
        setIsoDetail(updated);
        return updated;
    };

    /** Re-reads the open request and the ISO queue, for a request that changed since it was opened (409). */
    const reloadIsoItem = async () => {
        if (isoSelectedId) {
            await loadIsoDetail(isoSelectedId);
        }
        setIsoRefreshNonce((nonce) => nonce + 1);
    };

    useEffect(() => {
        let selectedId = selectedItem ?? isoSelectedId;
        let tempId = `expert-queue-view-card-${selectedItem ?? isoSelectedId}`;
        if (!selectedId || !tempId || !aiAssistantSectionRef.current) return;

        const element =
            aiAssistantSectionRef.current.querySelector<HTMLElement>(
                `#${CSS.escape(tempId)}`
            );

        if (!element) return;

        element.scrollIntoView({
            behavior: "smooth",
            block: "center",
            inline: "center",
        });
    }, [selectedItem, isoSelectedId]);

    useEffect(() => {
        if (!selectedItem) {
            return;
        }

        void (async () => {
            const accessToken = await getAccessToken();
            await fetchSelectedItemDetail(selectedItem, accessToken);
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedItem]);

    // An item that is with the ISO or being written changes without anyone here doing anything (the ISO decides in Jira,
    // the write finishes). Re-read it quietly, without the loading state that would close what the person is typing.
    const polledItemId =
        selectedItemDetail && POLLED_STATUSES.has(selectedItemDetail.status)
            ? selectedItemDetail.id
            : null;
    const polledStatus = selectedItemDetail?.status ?? null;
    useEffect(() => {
        if (!polledItemId || !polledStatus) {
            return;
        }

        const timer = window.setInterval(() => {
            if (document.visibilityState === "hidden") {
                return;
            }

            void (async () => {
                try {
                    const accessToken = await getAccessToken();
                    const latest = await api.getQueueItem(
                        polledItemId,
                        accessToken
                    );
                    if (
                        useAidwopsStore.getState().selectedItem !== polledItemId
                    ) {
                        return;
                    }

                    setSelectedItemDetail(latest);
                    if (latest.status !== polledStatus) {
                        await reloadQueue(accessToken);
                    }
                } catch {
                    // Keep what is shown; the next tick tries again
                }
            })();
        }, ITEM_POLL_MS);

        return () => window.clearInterval(timer);
        // getAccessToken, reloadQueue and setSelectedItemDetail follow unstable identities; the item and its status are what matter.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [polledItemId, polledStatus]);

    // What approving does in this environment, said once in the queue header. Not everyone may read it; then there is simply no note.
    const [hitlMode, setHitlMode] = useState<HitlHealth["mode"] | null>(null);
    useEffect(() => {
        if (!isQueueView) {
            return;
        }

        let isCurrent = true;
        void (async () => {
            try {
                const health = await api.getHitlHealth(await getAccessToken());
                if (isCurrent) {
                    setHitlMode(health.mode);
                }
            } catch {
                // No note
            }
        })();

        return () => {
            isCurrent = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isQueueView]);
    // Intune items only ever create Jira tasks, so the Zscaler mode does not apply to them.
    const modeNote =
        queuePersona === "intune" || hitlMode === null
            ? undefined
            : hitlMode === "staging"
              ? "Staging: an ISO approval is recorded, but nothing is written to Zscaler."
              : "Apply mode: an ISO approval writes the change to the dev tenant.";

    useEffect(() => {
        const trimmedInitialQuestion = initialQuestion?.trim() ?? "";

        if (!trimmedInitialQuestion || initialQuestionSubmittedRef.current) {
            return;
        }

        initialQuestionSubmittedRef.current = true;
        void submitPrompt(trimmedInitialQuestion, getAccessToken, askContext);
        onInitialQuestionHandled?.();
    }, [initialQuestion, onInitialQuestionHandled]);

    // Same path as every other message, so an edited queue command still gets its queue, item or draft back (handledBy and ui).
    const resubmitMessage = () => {
        const trimmedPrompt = editingDraft.trim();

        if (!trimmedPrompt || useAidwopsStore.getState().isSubmitting) {
            return;
        }

        setEditingMessageId(null);
        setEditingDraft("");
        void submitPrompt(trimmedPrompt, getAccessToken, askContext);
    };

    const handleInputKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
        if (event.key === "Enter" && !event.shiftKey) {
            event.preventDefault();
            void submitPrompt(draft, getAccessToken, askContext);
        }
    };

    const visibleMessages = messages;

    return (
        <div
            className={`aidwops-assistant-layout${selectedItem || isoSelectedId ? " aidwops-assistant-layout--split" : ""}`}
        >
            <section
                className="aidwops-assistant"
                aria-label="Aidwops AI chatbot"
                ref={aiAssistantSectionRef}
            >
                <div
                    className={`aidwops-assistant__messages ${visibleMessages.length > 0 ? "aidwops-assistant__messages--active" : ""}`}
                >
                    {visibleMessages.length === 0 ? (
                        <>
                            <SearchLanding
                                onQueueBannerClick={onQueueBannerClick}
                                context={askContext}
                            />
                            {isQueueView &&
                                queueRole !== "iso" &&
                                isQueueLoading && (
                                    <p className="expert-queue-view-empty triage-loading">
                                        <EonUiPreloaderSquares />
                                        Loading the expert queue... the first
                                        load can take up to 20 seconds.
                                    </p>
                                )}
                        </>
                    ) : (
                        visibleMessages.map((message, index) => (
                            <div
                                key={message.id}
                                ref={
                                    message.role === "user" &&
                                    index === visibleMessages.length - 1
                                        ? latestQuestionRef
                                        : undefined
                                }
                                className={`aidwops-assistant__message aidwops-assistant__message--${message.role}`}
                            >
                                {message.role === "assistant" && (
                                    <div
                                        className="aidwops-assistant__message-icon"
                                        aria-hidden="true"
                                    >
                                        <Icon name="AiIcon" />
                                    </div>
                                )}

                                <div className="aidwops-assistant__message-body">
                                    {editingMessageId === message.id ? (
                                        <div className="aidwops-assistant__edit">
                                            <textarea
                                                className="aidwops-assistant__edit-textarea"
                                                value={editingDraft}
                                                onChange={(event) => {
                                                    setEditingDraft(
                                                        event.target.value
                                                    );
                                                }}
                                                onKeyDown={(event) => {
                                                    if (
                                                        event.key === "Enter" &&
                                                        !event.shiftKey
                                                    ) {
                                                        event.preventDefault();
                                                        void resubmitMessage();
                                                    }

                                                    if (
                                                        event.key === "Escape"
                                                    ) {
                                                        setEditingMessageId(
                                                            null
                                                        );
                                                        setEditingDraft("");
                                                    }
                                                }}
                                                autoFocus
                                            />
                                            <div className="aidwops-assistant__edit-actions">
                                                <button
                                                    type="button"
                                                    className="aidwops-assistant__secondary-button"
                                                    onClick={() => {
                                                        setEditingMessageId(
                                                            null
                                                        );
                                                        setEditingDraft("");
                                                    }}
                                                >
                                                    Cancel
                                                </button>
                                                <button
                                                    type="button"
                                                    className="aidwops-assistant__primary-button"
                                                    disabled={
                                                        !editingDraft.trim() ||
                                                        isSubmitting
                                                    }
                                                    onClick={() =>
                                                        void resubmitMessage()
                                                    }
                                                >
                                                    Resubmit
                                                </button>
                                            </div>
                                        </div>
                                    ) : (
                                        <>
                                            <div className="aidwops-assistant__message-row">
                                                <div className="aidwops-assistant__message-content">
                                                    {message.role ===
                                                    "assistant" ? (
                                                        <>
                                                            <FormattedAssistantContent
                                                                content={
                                                                    message.content
                                                                }
                                                            />
                                                            <div
                                                                style={{
                                                                    height: "16px",
                                                                }}
                                                            />
                                                            {message.ui
                                                                ?.type ===
                                                            "whitelist_request" ? (
                                                                <AidwopsAiAssistantWhitelistRequestResponse
                                                                    url={
                                                                        message
                                                                            .ui
                                                                            .url
                                                                    }
                                                                    onOpenExpertQueue={
                                                                        onOpenExpertQueue
                                                                    }
                                                                />
                                                            ) : message.ui
                                                                  ?.type ===
                                                              "ssl_bypass_request" ? (
                                                                <AidwopsAiAssistantSslBypassRequestResponse
                                                                    url={
                                                                        message
                                                                            .ui
                                                                            .url
                                                                    }
                                                                    onOpenExpertQueue={
                                                                        onOpenExpertQueue
                                                                    }
                                                                />
                                                            ) : (
                                                                message.handledBy ===
                                                                    "queue" &&
                                                                message.ui &&
                                                                (message.ui
                                                                    .type ===
                                                                "queue" ? (
                                                                    <ExpertQueueView
                                                                        showCustomFilter={
                                                                            index ===
                                                                            0
                                                                        }
                                                                        // A new key starts the list over on the right tab when the status filter changes.
                                                                        key={`${index}-${queueStatus}`}
                                                                        persona={
                                                                            queuePersona
                                                                        }
                                                                        status={
                                                                            index ===
                                                                            0
                                                                                ? queueStatus
                                                                                : "pending"
                                                                        }
                                                                        role={
                                                                            index ===
                                                                            0
                                                                                ? queueRole
                                                                                : undefined
                                                                        }
                                                                        onRoleChange={
                                                                            index ===
                                                                            0
                                                                                ? onQueueRoleChange
                                                                                : undefined
                                                                        }
                                                                        onPersonaChange={
                                                                            index ===
                                                                            0
                                                                                ? onQueuePersonaChange
                                                                                : undefined
                                                                        }
                                                                        onStatusChange={
                                                                            index ===
                                                                            0
                                                                                ? onQueueStatusChange
                                                                                : undefined
                                                                        }
                                                                        onRaise={
                                                                            index ===
                                                                                0 &&
                                                                            queuePersona !==
                                                                                "intune"
                                                                                ? handleQueueRaise
                                                                                : undefined
                                                                        }
                                                                        onOpenItem={
                                                                            queueRole ===
                                                                            "iso"
                                                                                ? openIsoItem
                                                                                : undefined
                                                                        }
                                                                        selectedItemId={
                                                                            queueRole ===
                                                                            "iso"
                                                                                ? isoSelectedId
                                                                                : undefined
                                                                        }
                                                                        modeNote={
                                                                            index ===
                                                                            0
                                                                                ? modeNote
                                                                                : undefined
                                                                        }
                                                                        queue={{
                                                                            summary:
                                                                                message
                                                                                    .ui
                                                                                    .summary,
                                                                            items: message
                                                                                .ui
                                                                                .items,
                                                                        }}
                                                                    />
                                                                ) : (
                                                                    <ChatUiBlock
                                                                        ui={
                                                                            message.ui
                                                                        }
                                                                    />
                                                                ))
                                                            )}
                                                        </>
                                                    ) : (
                                                        message.content
                                                    )}
                                                </div>
                                                {message.role === "user" && (
                                                    <button
                                                        type="button"
                                                        className="aidwops-assistant__edit-button"
                                                        onClick={() => {
                                                            setEditingMessageId(
                                                                message.id
                                                            );
                                                            setEditingDraft(
                                                                message.content
                                                            );
                                                        }}
                                                        aria-label="Edit message"
                                                    >
                                                        <Icon name="edit" />
                                                    </button>
                                                )}
                                            </div>
                                        </>
                                    )}
                                </div>
                            </div>
                        ))
                    )}

                    {isSubmitting && <AiLoader />}
                </div>

                {visibleMessages.length > 0 && (
                    <div className="form-wrapper">
                        {/* <div className="chips-suggestions"> */}
                        {/* {initialPrompts.map((prompt) => ( */}
                        {/* {AIDWOPS_ASSISTANT_PROMPTS.map((prompt) => (
                                <button
                                    key={prompt}
                                    className="chip"
                                    onClick={() => {
                                        setDraft(prompt);
                                        submitPrompt(
                                            prompt,
                                            getAccessToken,
                                            askContext
                                        );
                                    }}
                                >
                                    {prompt}
                                </button>
                            ))}
                        </div> */}
                        <SearchInputBox
                            value={draft}
                            setValue={setDraft}
                            submit={() =>
                                void submitPrompt(
                                    draft,
                                    getAccessToken,
                                    askContext
                                )
                            }
                            isSubmitting={isSubmitting}
                            textareaRef={textareaRef}
                            onKeyDown={handleInputKeyDown}
                            rows={2}
                            fullWidth
                        />
                    </div>
                )}
            </section>
            {selectedItem && (
                <aside
                    className="aidwops-assistant-review"
                    aria-label="Review task"
                >
                    {isSelectedItemLoading && (
                        <div className="aidwops-assistant-review__loading-box">
                            <button
                                type="button"
                                className="aidwops-assistant-review__back"
                                onClick={() => setSelectedItem(null)}
                            >
                                <Icon name="chevron_small_left" />
                                Back
                            </button>
                            <p className="expert-queue-item-detail-card-section-text triage-loading">
                                <EonUiPreloaderSquares />
                                Loading item details...
                            </p>
                        </div>
                    )}
                    {selectedItemError && (
                        <div className="aidwops-assistant-review__error-box">
                            <button
                                type="button"
                                className="aidwops-assistant-review__back"
                                onClick={() => setSelectedItem(null)}
                            >
                                <Icon name="close" />
                                Close
                            </button>
                            <p className="ac-alert-detail">
                                {selectedItemError}
                            </p>
                        </div>
                    )}
                    {!isSelectedItemLoading &&
                        !selectedItemError &&
                        selectedItemDetail && (
                            <QueueItemDetailCard
                                item={selectedItemDetail}
                                onAction={handleItemAction}
                                onComment={handleItemComment}
                                onReload={handleQueueReload}
                            />
                        )}
                </aside>
            )}
            {isoSelectedId && (
                <aside
                    className="aidwops-assistant-review"
                    aria-label="ISO review"
                >
                    {isoJiraWarning && (
                        <div className="ac-alert" role="alert">
                            <p className="ac-alert-detail">{isoJiraWarning}</p>
                        </div>
                    )}
                    {isIsoDetailLoading && (
                        <div className="aidwops-assistant-review__loading-box">
                            <button
                                type="button"
                                className="aidwops-assistant-review__back"
                                onClick={closeIsoItem}
                            >
                                <Icon name="chevron_small_left" />
                                Back
                            </button>
                            <p className="expert-queue-item-detail-card-section-text triage-loading">
                                <EonUiPreloaderSquares />
                                Loading the request...
                            </p>
                        </div>
                    )}
                    {isoDetailError && (
                        <div className="aidwops-assistant-review__error-box">
                            <button
                                type="button"
                                className="aidwops-assistant-review__back"
                                onClick={closeIsoItem}
                            >
                                <Icon name="close" />
                                Close
                            </button>
                            <p className="ac-alert-detail">{isoDetailError}</p>
                        </div>
                    )}
                    {!isIsoDetailLoading && !isoDetailError && isoDetail && (
                        <IsoRequestDetail
                            item={isoDetail}
                            onAction={handleIsoAction}
                            onComment={handleIsoComment}
                            onReload={reloadIsoItem}
                            onDismiss={closeIsoItem}
                        />
                    )}
                </aside>
            )}
        </div>
    );
}
